import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { sessionService } from '../../services/session.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import LegalLinks from '../../components/legal/LegalLinks';
import { TERMS_VERSION } from '../../constants/legal';
import { useTranslation } from 'react-i18next';
import { chatService } from '../../services/chat.service';
import { useAuth } from '../../contexts/AuthContext';

type AppealSessionResponse = {
  sessionId: string;
  appealContext?: Record<string, any> | null;
  slotValues?: Record<string, any>;
  chatState?: { categories?: string[] } | null;
  ticket?: Record<string, any> | null;
  contextAtTime?: { weather?: { summary?: any; resolvedAddress?: string } } | null;
  appealLetter?: { text?: string } | null;
  termsAcceptedAt?: number | null;
  termsAcceptedVersion?: string | null;
  humanReviewDisclaimerAcceptedAt?: number | null;
  humanReviewDisclaimerVersion?: string | null;
  generationCount?: number;
  generationLimit?: number;
};

type ChatMessage = {
  role: 'system' | 'user';
  content: string;
  helperText?: string | null;
  stepKey?: string | null;
  slotKey?: string | null;
  createdAt?: any;
};

type CollectedAnswer = {
  stepKey: string;
  question: string;
  answer: string;
};

type ReviewState = {
  canSubmit: boolean;
  disableReason: 'NONE' | 'ALREADY_SUBMITTED' | string;
  remaining: number;
  resetAt?: any | null;
  submittedRecently?: boolean;
  requestStatus?: string | null;
  requestId?: string | null;
};

const buildCollectedAnswers = (messages: ChatMessage[]): CollectedAnswer[] => {
  const questionByStep = new Map<string, string>();
  const answerByStep = new Map<string, string[]>();
  const stepOrder: string[] = [];

  const isValidationMessage = (content: string) => {
    const normalized = content.toLowerCase();
    return normalized.includes('missing required answer') || normalized.includes('请提供有效的回答');
  };

  messages.forEach((message) => {
    const stepKey = message.stepKey || message.slotKey || null;
    if (!stepKey) return;
    if (message.role === 'system') {
      if (!message.content || isValidationMessage(message.content)) return;
      if (!questionByStep.has(stepKey)) {
        questionByStep.set(stepKey, message.content);
        stepOrder.push(stepKey);
      }
      return;
    }
    if (message.role === 'user') {
      const existing = answerByStep.get(stepKey) || [];
      existing.push(message.content);
      answerByStep.set(stepKey, existing);
    }
  });

  const collected: CollectedAnswer[] = [];
  stepOrder.forEach((stepKey) => {
    const answers = answerByStep.get(stepKey);
    if (!answers || answers.length === 0) return;
    const merged = answers
      .map((value, index) => (index === 0 ? value : `Additional details: ${value}`))
      .join('\n\n');
    collected.push({
      stepKey,
      question: questionByStep.get(stepKey) || stepKey,
      answer: merged,
    });
  });

  answerByStep.forEach((answers, stepKey) => {
    if (questionByStep.has(stepKey)) return;
    if (!answers || answers.length === 0) return;
    const merged = answers
      .map((value, index) => (index === 0 ? value : `Additional details: ${value}`))
      .join('\n\n');
    collected.push({
      stepKey,
      question: stepKey,
      answer: merged,
    });
  });

  return collected;
};

const formatFirestoreTimestamp = (value: any) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object' && typeof value._seconds === 'number') {
    const date = new Date(value._seconds * 1000);
    return date.toISOString();
  }
  return String(value);
};

const AppealSummaryPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['tickets', 'common']);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<AppealSessionResponse | null>(null);
  const [letterText, setLetterText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reviewEmail, setReviewEmail] = useState('');
  const [reviewConsent, setReviewConsent] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewState, setReviewState] = useState<ReviewState | null>(null);
  const [usage, setUsage] = useState<{
    limitCents: number;
    remainingCents: number;
  } | null>(null);
  const { user } = useAuth();
  const [savingLetter, setSavingLetter] = useState(false);
  const [extraNote, setExtraNote] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [billingOverCap, setBillingOverCap] = useState<boolean | null>(null);
  const [billingCapUsd, setBillingCapUsd] = useState<number | null>(null);
  const [billingCostUsd, setBillingCostUsd] = useState<number | null>(null);
  const [usingHumanReview, setUsingHumanReview] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);

  useEffect(() => {
    if (!sessionId) return;
    let active = true;
    const loadSession = async () => {
      setLoading(true);
      try {
        const response = await sessionService.getAppealSession(sessionId);
        if (!active) return;
        setSession(response);
        setLetterText(response?.appealLetter?.text || '');
        setExtraNote(response?.appealContext?.extraNote || '');
        const messageResponse = await chatService.getMessages(sessionId, 200);
        if (!active) return;
        setChatMessages(messageResponse as ChatMessage[]);
        try {
          const billingResponse = await sessionService.getBillingMe(true);
          if (!active) return;
          setBillingOverCap(!!billingResponse?.isOverCap);
          setBillingCapUsd(
            typeof billingResponse?.capUsd === 'number' ? billingResponse.capUsd : null
          );
          setBillingCostUsd(
            typeof billingResponse?.costUsdTotal === 'number' ? billingResponse.costUsdTotal : null
          );
        } catch (error) {
          setBillingOverCap(null);
          setBillingCapUsd(null);
          setBillingCostUsd(null);
        }
      } catch (error: any) {
        toast.error(error.message || t('appealSummary.loadFailed', { ns: 'tickets' }));
      } finally {
        if (active) setLoading(false);
      }
    };
    loadSession();
    return () => {
      active = false;
    };
  }, [sessionId]);

  useEffect(() => {
    let active = true;
    const loadUsage = async () => {
      try {
        const response = await sessionService.getUsageMe();
        if (active) {
          setUsage({ limitCents: response.limitCents, remainingCents: response.remainingCents });
        }
      } catch (error) {
        // ignore usage errors
      }
    };
    loadUsage();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!sessionId || !session) return;
    const accepted =
      session?.termsAcceptedAt &&
      session?.termsAcceptedVersion === TERMS_VERSION;
    if (!accepted) {
      navigate('/upload', { replace: true });
    }
  }, [session, sessionId, navigate]);

  useEffect(() => {
    if (user?.email) {
      setReviewEmail((prev) => (prev ? prev : user.email || ''));
    }
  }, [user?.email]);


  const generationCount = session?.generationCount ?? 0;
  const generationLimit = session?.generationLimit ?? 10;
  const limitReached = generationCount >= generationLimit;
  const hasTermsConsent =
    !!session?.termsAcceptedAt &&
    session?.termsAcceptedVersion === TERMS_VERSION;
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reviewEmail.trim());

  const loadReviewState = useCallback(async () => {
    if (!sessionId) return;
    const response = await sessionService.getReviewRequest(sessionId);
    const request = response?.request || null;
    const nextReviewState = response?.reviewState || null;
    setReviewState(nextReviewState);
    if (request?.userEmail) {
      setReviewEmail((prev) => prev || request.userEmail);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    loadReviewState().catch(() => setReviewState(null));
  }, [sessionId, loadReviewState]);

  const handleUseHumanReview = async () => {
    if (!sessionId || usingHumanReview || reviewSubmitting) return;
    if (!letterText.trim()) {
      toast.error('Appeal letter is required for review.');
      return;
    }
    if (!isEmailValid) {
      setReviewError('Please enter a valid email address.');
      return;
    }
    if (!reviewConsent) {
      setReviewError('Please confirm you agree to be contacted by email.');
      return;
    }
    setReviewSubmitting(true);
    setUsingHumanReview(true);
    setReviewError(null);
    try {
      await sessionService.requestExperiencedReview({
        caseId: sessionId,
        userEmail: reviewEmail.trim(),
        letterText: letterText.trim(),
      });
      await loadReviewState();
      toast.success('Submitted. We’ll email you within 3 business days.');
    } catch (error: any) {
      const message = error?.message || 'Unable to request experienced review.';
      setReviewError(message);
      toast.error(message);
    } finally {
      setReviewSubmitting(false);
      setUsingHumanReview(false);
    }
  };

  const renderAnswerBlock = (children: React.ReactNode) => (
    <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-800 space-y-1">{children}</div>
  );

  const collectedAnswers = useMemo(() => buildCollectedAnswers(chatMessages), [chatMessages]);
  const weatherContext = session?.contextAtTime?.weather || null;
  const weatherSummary = weatherContext?.summary || null;
  const usageLimitReached = usage ? usage.remainingCents <= 0 : false;
  const reviewRemaining = reviewState?.remaining ?? 0;
  const reviewDisableReason = reviewState?.disableReason || 'NONE';
  const reviewAlreadySubmitted = reviewDisableReason === 'ALREADY_SUBMITTED';
  const reviewNoCredits = reviewDisableReason === 'NO_CREDITS';
  const reviewRequestId = reviewState?.requestId || null;

  const formattedResetAt = useMemo(() => {
    if (!reviewState?.resetAt) return null;
    const iso = formatFirestoreTimestamp(reviewState.resetAt);
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString();
  }, [reviewState?.resetAt]);


  const ticketMeta = useMemo(
    () => ({
      ticketNumber: session?.ticket?.ticketNumber,
      plateNumber: session?.ticket?.plateNumber,
      violationDateTime: formatFirestoreTimestamp(session?.ticket?.violationDateTime),
    }),
    [session]
  );

  const handleGenerate = async () => {
    if (!sessionId) return;
    if (limitReached) return;
    if (billingOverCap || usageLimitReached) {
      setErrorMessage('You’ve reached your $5 usage limit. Please upgrade or contact support.');
      return;
    }
    setGenerating(true);
    setErrorMessage(null);
    try {
      const response = await sessionService.generateAppealLetter(sessionId, {
        language: i18n.language,
        currentLetter: letterText || undefined,
        termsAccepted: hasTermsConsent,
      });
      setLetterText(response.text || '');
      setSession((prev) =>
        prev
          ? {
              ...prev,
              generationCount: response.count,
              generationLimit: response.limit,
            }
          : prev
      );
    } catch (error: any) {
      if (error?.response?.data?.error === 'BILLING_CAP_REACHED' || error?.response?.data?.error === 'LIMIT_EXCEEDED') {
        setBillingOverCap(true);
        setErrorMessage('You’ve reached your $5 usage limit. Please upgrade or contact support.');
      } else if (error?.message?.includes('GENERATION_LIMIT_REACHED')) {
        setErrorMessage(t('appealSummary.limitReached', { ns: 'tickets' }));
        setSession((prev) =>
          prev
            ? {
                ...prev,
                generationCount: prev.generationLimit ?? 10,
              }
            : prev
        );
      } else {
        setErrorMessage(error.message || t('appealSummary.generateFailed', { ns: 'tickets' }));
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveLetter = async () => {
    if (!sessionId) return;
    if (!letterText.trim()) {
      setErrorMessage('Appeal letter text is required.');
      return;
    }
    if (letterText.trim().length > 2500) {
      setErrorMessage('Letter edits must be 2500 characters or less.');
      return;
    }
    setSavingLetter(true);
    setErrorMessage(null);
    try {
      let sanitizedLetter = letterText;
      sanitizedLetter = sanitizedLetter.replace(/https?:\/\/\S+/gi, '');
      sanitizedLetter = sanitizedLetter.replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '');
      sanitizedLetter = sanitizedLetter.replace(/\+?\d[\d\s().-]{7,}\d/g, '');
      sanitizedLetter = sanitizedLetter.replace(/<[^>]*>/g, '');
      sanitizedLetter = sanitizedLetter.replace(/[*_`~>#]/g, '');
      sanitizedLetter = sanitizedLetter.replace(/[!?.]{4,}/g, (match) => match.slice(0, 3));
      sanitizedLetter = sanitizedLetter.replace(/[ \t]{2,}/g, ' ');
      sanitizedLetter = sanitizedLetter.replace(/\n{3,}/g, '\n\n');
      sanitizedLetter = sanitizedLetter.trim().slice(0, 2500);
      const response = await sessionService.updateAppealLetter(sessionId, { text: sanitizedLetter });
      setLetterText(response.text);
      toast.success(t('appealSummary.saved', { ns: 'tickets', defaultValue: 'Saved' }));
    } catch (error: any) {
      setErrorMessage(error.message || t('appealSummary.saveFailed', { ns: 'tickets', defaultValue: 'Failed to save' }));
    } finally {
      setSavingLetter(false);
    }
  };

  const sanitizeNote = (value: string) => {
    let text = value;
    text = text.replace(/https?:\/\/\S+/gi, '');
    text = text.replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '');
    text = text.replace(/\+?\d[\d\s().-]{7,}\d/g, '');
    text = text.replace(/<[^>]*>/g, '');
    text = text.replace(/[*_`~>#]/g, '');
    text = text.replace(/[!?.]{4,}/g, (match) => match.slice(0, 3));
    text = text.replace(/[ \t]{2,}/g, ' ');
    text = text.replace(/\n{3,}/g, '\n\n');
    return text.trim().slice(0, 100);
  };

  const validateNote = (value: string) => {
    if (value.length > 100) return 'Note exceeds 100 characters.';
    if (/https?:\/\/\S+/i.test(value)) return 'URLs are not allowed.';
    if (/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i.test(value)) return 'Emails are not allowed.';
    if (/\+?\d[\d\s().-]{7,}\d/.test(value)) return 'Phone numbers are not allowed.';
    return null;
  };

  const handleSaveNote = async () => {
    if (!sessionId) return;
    const error = validateNote(extraNote);
    if (error) {
      setNoteError(error);
      return;
    }
    setSavingNote(true);
    setNoteError(null);
    try {
      const sanitized = sanitizeNote(extraNote);
      const response = await sessionService.updateExtraNote(sessionId, { note: sanitized });
      setExtraNote(response.note || '');
      toast.success(t('appealSummary.extraNote.saved', { ns: 'tickets', defaultValue: 'Note saved' }));
    } catch (error: any) {
      setNoteError(error.message || t('appealSummary.extraNote.saveFailed', { ns: 'tickets', defaultValue: 'Failed to save note' }));
    } finally {
      setSavingNote(false);
    }
  };

  const handleContinue = () => {
    if (!sessionId) return;
    navigate(`/ticket/${sessionId}/filing`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text={t('appealSummary.loading', { ns: 'tickets' })} />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-gray-700">{t('appealSummary.loadError', { ns: 'tickets' })}</p>
          <button className="btn-primary mt-4" onClick={() => navigate('/upload')}>
            {t('appealSummary.startNew', { ns: 'tickets' })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-10">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <span className="rounded-full border border-[#C6A87C]/30 bg-[#C6A87C]/10 px-2 py-1 text-xs font-semibold uppercase tracking-wider text-[#C6A87C]">
                Premium Analysis
              </span>
              <span className="text-sm text-slate-400">Case #{sessionId?.slice(-6) || '—'}</span>
            </div>
            <h1 className="font-display text-4xl font-bold text-slate-900">Review Your Appeal</h1>
            <p className="mt-2 max-w-2xl text-slate-600">
              Refine your defense before finalizing. Our AI has drafted a letter based on the evidence provided.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              disabled
              className="flex items-center gap-2 rounded-lg border border-slate-200 px-6 py-2.5 text-sm font-medium text-slate-500 transition-all hover:bg-slate-50 disabled:cursor-not-allowed"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12m0 0l-4-4m4 4l4-4M4 21h16" />
              </svg>
              Download PDF
            </button>
            <button
              type="button"
              onClick={handleContinue}
              className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#C6A87C] to-[#B09265] px-6 py-2.5 text-sm font-medium text-white shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
              Submit Appeal
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 items-start">
          <div className="space-y-6 lg:col-span-5">
            <div className="rounded-xl border border-[#E9DFCD] bg-[#F8F2E6] p-6 shadow-[0_0_15px_rgba(198,168,124,0.08)]">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display text-xl font-bold text-[#7C5E3D]">
                    {t('appealSummary.extraNote.title', { ns: 'tickets', defaultValue: 'Optional: Extra note' })}
                  </h3>
                  <p className="mt-1 text-sm text-[#9A7A52]">
                    {t('appealSummary.extraNote.helper', {
                      ns: 'tickets',
                      defaultValue: 'Add any extra context you want included in the letter (optional).',
                    })}
                  </p>
                </div>
                <div className="rounded-full bg-white/80 p-2 text-[#C6A87C] shadow-sm">✍️</div>
              </div>
              <div className="relative mt-4">
                <textarea
                  className="min-h-[140px] w-full rounded-lg border border-[#E9DFCD] bg-white p-4 text-sm text-slate-700 shadow-inner placeholder:text-slate-400 focus:ring-2 focus:ring-[#C6A87C] focus:border-transparent resize-none"
                  value={extraNote}
                  onChange={(event) => {
                    setExtraNote(event.target.value);
                    if (noteError) setNoteError(null);
                  }}
                  maxLength={100}
                  placeholder={t('appealSummary.extraNote.placeholder', {
                    ns: 'tickets',
                    defaultValue: 'E.g., I stopped briefly because my GPS failed in heavy rain...',
                  })}
                />
                <div className="absolute bottom-3 right-3 text-xs text-slate-400">{extraNote.length}/100</div>
              </div>
              {noteError && <div className="mt-2 text-sm text-red-600">{noteError}</div>}
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs font-medium text-[#9A7A52]">
                  {t('appealSummary.extraNote.disclaimer', {
                    ns: 'tickets',
                    defaultValue: 'This note may be included in your generated appeal.',
                  })}
                </span>
                <button
                  type="button"
                  onClick={handleSaveNote}
                  disabled={savingNote}
                  className="rounded-lg bg-[#C6A87C] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#B09265] disabled:opacity-60"
                >
                  {savingNote
                    ? t('appealSummary.extraNote.saving', { ns: 'tickets', defaultValue: 'Saving...' })
                    : t('appealSummary.extraNote.save', { ns: 'tickets', defaultValue: 'Save note' })}
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="px-1 text-xs font-bold uppercase tracking-wider text-slate-400">
                Collected information
              </h3>
              <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.02),0_2px_4px_-1px_rgba(0,0,0,0.02)] space-y-4">
                {collectedAnswers.length === 0 ? (
                  <div className="text-sm text-slate-500">
                    No evidence answers collected yet. Go back to Evidence to provide details.
                  </div>
                ) : (
                  collectedAnswers.map((item) => (
                    <div
                      key={item.stepKey}
                      className="rounded-lg border border-slate-100 bg-slate-50 p-3"
                    >
                      <p className="text-xs font-medium text-slate-500">{item.question}</p>
                      <p className="mt-1 text-sm font-semibold text-slate-800">{item.answer}</p>
                    </div>
                  ))
                )}
                {weatherSummary && (
                  <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                    <p className="text-xs font-medium text-slate-500">Weather</p>
                    <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-slate-800">
                      <span>☁️</span>
                      <span>
                        {weatherSummary.weatherLabel || 'Unknown'}, {weatherSummary.tempC ?? '--'}°C · Precip{' '}
                        {weatherSummary.precipitationMm ?? '--'} mm
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-slate-100 bg-white p-6 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.02),0_2px_4px_-1px_rgba(0,0,0,0.02)]">
              <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-slate-400">Case status</h3>
              <div className="flex items-center gap-4 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[#E9DFCD] bg-[#F8F2E6] text-[#C6A87C]">
                  <svg
                    viewBox="0 0 24 24"
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12l2 2 4-4M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="font-bold text-slate-900">Ready for Review</p>
                  <p className="text-xs text-slate-500">Success confidence: High</p>
                </div>
              </div>
              <div className="border-t border-slate-100 pt-4">
                <div className="mb-2 flex items-center justify-between text-xs font-semibold text-slate-500">
                  <span>Ticket Details</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Ticket #</span>
                    <span className="font-mono font-medium text-slate-800">
                      {ticketMeta.ticketNumber || t('appealSummary.notAvailable', { ns: 'tickets' })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Plate</span>
                    <span className="rounded bg-slate-100 px-2 font-mono font-medium text-slate-800">
                      {ticketMeta.plateNumber || t('appealSummary.notAvailable', { ns: 'tickets' })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Date &amp; Time</span>
                    <span className="text-right font-medium text-slate-800">
                      {ticketMeta.violationDateTime || t('appealSummary.notAvailable', { ns: 'tickets' })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_10px_40px_-10px_rgba(0,0,0,0.08)]">
              <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-6 py-3">
                <div className="flex gap-2">
                  <span className="h-3 w-3 rounded-full bg-red-400" />
                  <span className="h-3 w-3 rounded-full bg-amber-400" />
                  <span className="h-3 w-3 rounded-full bg-emerald-400" />
                </div>
                <div className="text-xs font-mono uppercase text-slate-400">appeal_draft_v1.pdf</div>
                <div className="flex gap-3 text-slate-400">
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 9V3h12v6M6 18h12v3H6v-3zm-2-8h16a2 2 0 012 2v5H2v-5a2 2 0 012-2z"
                    />
                  </svg>
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 20h4l10-10-4-4L4 16v4zm12-14l2 2"
                    />
                  </svg>
                </div>
              </div>
              <div className="space-y-4 px-6 py-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">Appeal letter</h2>
                    <p className="text-sm text-slate-600">
                      {t('appealSummary.generationsUsed', { ns: 'tickets', count: generationCount, limit: generationLimit })}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={generating || limitReached || !hasTermsConsent || billingOverCap === true || usageLimitReached}
                    className="rounded-lg bg-[#C6A87C] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#B09265] disabled:opacity-60"
                  >
                    {generating ? t('appealSummary.generating', { ns: 'tickets' }) : t('appealSummary.generateButton', { ns: 'tickets' })}
                  </button>
                </div>
                {billingOverCap && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                    You’ve reached your $5 usage cap.
                    {billingCostUsd !== null && billingCapUsd !== null
                      ? ` Usage: $${billingCostUsd.toFixed(2)} of $${billingCapUsd.toFixed(2)}.`
                      : ''}
                  </div>
                )}
                {usageLimitReached && !billingOverCap && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    You’ve reached your $5 usage limit. Please upgrade or contact support.
                  </div>
                )}

                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                  By generating this letter, you confirm you agree to our Terms and understand this is not legal advice.
                  <div className="mt-2 text-xs text-slate-500">
                    <LegalLinks />
                  </div>
                </div>

                {limitReached && (
                  <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4 text-yellow-800">
                    {t('appealSummary.limitReached', { ns: 'tickets' })}
                  </div>
                )}
                {errorMessage && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-4 text-red-800">
                    {errorMessage}
                  </div>
                )}

                <textarea
                  className="min-h-[480px] w-full rounded-lg border border-slate-200 bg-white p-5 text-sm text-slate-800 shadow-inner"
                  value={letterText}
                  onChange={(event) => setLetterText(event.target.value)}
                  maxLength={2500}
                  placeholder={t('appealSummary.noLetter', { ns: 'tickets', defaultValue: 'Generate a letter to edit it here.' })}
                />
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>{letterText.length}/2500</span>
                  <span>{t('appealSummary.noLetter', { ns: 'tickets', defaultValue: 'Generate a letter to edit it here.' })}</span>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleSaveLetter}
                    disabled={savingLetter || !letterText.trim()}
                    className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 transition-colors hover:border-[#C6A87C] hover:text-[#C6A87C] disabled:opacity-60"
                  >
                    {savingLetter
                      ? t('appealSummary.saving', { ns: 'tickets', defaultValue: 'Saving...' })
                      : t('appealSummary.saveButton', { ns: 'tickets', defaultValue: 'Save changes' })}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-3">
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4 text-[#C6A87C]"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 2l2.5 2.5L18 5l.5 3.5L22 11l-3.5 2.5L18 17l-3.5.5L12 22l-2.5-4.5L6 17l-.5-3.5L2 11l3.5-2.5L6 5l3.5-.5L12 2z"
                    />
                  </svg>
                  Legal formatting verified
                </div>
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={generating || limitReached || !hasTermsConsent || billingOverCap === true || usageLimitReached}
                  className="text-xs font-medium text-[#C6A87C] hover:text-[#B09265] disabled:opacity-60"
                >
                  Regenerate with more details
                </button>
              </div>
            </div>

            {letterText.trim() && (
            <div className="mt-6 rounded-xl border border-[#E9DFCD] bg-[#FFFCF5] p-6 shadow-[0_12px_32px_-20px_rgba(15,23,42,0.25)]">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Experienced Review</h3>
                  <p className="mt-1 text-sm text-slate-600">
                    Optional human review by experienced dispute staff. Not legal advice.
                  </p>
                </div>
                {reviewRequestId && (
                  <span className="rounded-full border border-[#C6A87C]/40 bg-white px-2.5 py-1 text-xs font-semibold text-[#A7854F]">
                    Requested
                  </span>
                )}
              </div>

              <div className="mt-4 rounded-lg border border-[#E9DFCD] bg-white px-4 py-3 text-sm text-slate-700">
                <span className="font-semibold text-[#7C5E3D]">Remaining:</span>{' '}
                {reviewRemaining}
              </div>

              {formattedResetAt && (
                <p className="mt-3 text-xs text-slate-500">
                  Resets on: {formattedResetAt}
                </p>
              )}

              <div className="mt-4 text-xs text-slate-500">
                Expected response in 3 business days.
              </div>

              <div className="mt-4 space-y-3">
                <div>
                  <label className="text-sm font-semibold text-slate-900">Your email (for response)</label>
                  <input
                    type="email"
                    value={reviewEmail}
                    onChange={(event) => setReviewEmail(event.target.value)}
                    placeholder="you@example.com"
                    className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-[#C6A87C] focus:ring-[#C6A87C]"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    We’ll email you when the review is complete. Not legal advice.
                  </p>
                </div>

                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    checked={reviewConsent}
                    onChange={(event) => setReviewConsent(event.target.checked)}
                    className="rounded border-slate-300 text-[#C6A87C]"
                  />
                  I agree to be contacted by email regarding this review.
                </label>

                {reviewError && <div className="text-xs text-red-600">{reviewError}</div>}

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleUseHumanReview}
                    disabled={
                      !reviewState?.canSubmit ||
                      usingHumanReview ||
                      reviewSubmitting ||
                      !isEmailValid ||
                      !reviewConsent
                    }
                    className="rounded-lg bg-gradient-to-r from-[#C6A87C] to-[#B09265] px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {reviewAlreadySubmitted
                      ? 'Submitted'
                      : usingHumanReview || reviewSubmitting
                      ? 'Submitting...'
                      : 'Send for Experienced Review'}
                  </button>

                  {reviewAlreadySubmitted && (
                    <div className="text-xs text-slate-500">
                      You already have an active review for this case.
                    </div>
                  )}
                  {reviewNoCredits && (
                    <div className="text-xs text-slate-500">
                      No review credits remaining on your account.
                    </div>
                  )}
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppealSummaryPage;
